import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../shared/useFetchMoreCallback';
import { driverMetricsQuery, driverMetricsQueryVariables } from '../generated/driverMetricsQuery';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { driverMetricFieldsFragment } from '../models/DriverMetric';

/**
 * Get a list of driverMetrics from the store.
 * @param id
 * @param options
 */
export function useDriverMetrics(options: AsyncLoadPagedOptions = {}): AsyncLoadPagedResult<driverMetricsQuery> {
    // Query data we need from the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<driverMetricsQuery, driverMetricsQueryVariables>(
        gql`
        query driverMetricsQuery ($offset: Int, $first: Int) {
            items: driverMetrics (offset: $offset, first: $first) {
                ...driverMetricFields
            }
        }

        ${driverMetricFieldsFragment}
        `,
        {

            variables: {
                offset: options.startOffset,
                first: options.pageSize
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<driverMetricsQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: [...(prevResults?.items ?? []), ...(newResults?.items ?? [])]
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);
    
    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}
